import { OrderModel } from "../../../screens/portfolio-info/types/OrderModel";
import { PortfolioModel } from "../../../screens/portfolio-info/types/PortfolioModel";
import { PositionModel } from "../../../screens/portfolio-info/types/PositionModel";
import { PortfolioGroupModel } from "../../../screens/portfolio-info/types/PortfolioGroupModel";
import { PortfolioSettingsModel } from "../../../screens/portfolio-info/types/PortfolioSettingsModel";
import { PortfolioBalanceHistoryEntryModel } from "../../../screens/portfolio-info/types/PortfolioBalanceHistoryEntryModel";
import { portfolioMappers } from "../../mappers/PortfolioMappers";

class PortfolioModelService {
    copyPortfolio(
        portfolio: PortfolioModel,
        overrides: Partial<PortfolioModel> = {},
    ): PortfolioModel {
        return {
            ...portfolio,
            underlyingAsset: {
                ...portfolio.underlyingAsset,
            },
            portfolioGroup: this.copyPortfolioGroup(portfolio.portfolioGroup),
            positions: portfolio.positions.map(position => this.copyPosition(position)),
            orders: portfolio.orders.map(order => this.copyOrder(order)),
            portfolioBalanceHistory: portfolio.portfolioBalanceHistory.map(entry => this.copyBalanceHistoryEntry(entry)),
            portfolioSettings: portfolio.portfolioSettings !== null
                ? portfolioMappers.mapSettingsModelToModel(portfolio.portfolioSettings)
                : null,
            ...overrides,
        };
    }

    copyOrder(
        order: OrderModel,
        overrides: Partial<OrderModel> = {},
    ): OrderModel {
        return new OrderModel({
            externalId: order.externalId,
            exchangeOrderId: order.exchangeOrderId,
            internalId: order.internalId,
            autoOrderConfiguration: order.autoOrderConfiguration,
            state: order.state,
            targetPlatform: order.targetPlatform,
            openedPositionAt: order.openedPositionAt,
            expireAt: order.expireAt,
            impliedVolatilityAtOpen: order.impliedVolatilityAtOpen,
            customImpliedVolatility: order.customImpliedVolatility,
            customImpliedVolatilityStep: order.customImpliedVolatilityStep,
            type: order.type,
            strike: order.strike,
            symbol: order.symbol,
            size: order.size,
            sizeStep: order.sizeStep,
            fee: order.fee ?? 0,
            processingType: order.processingType,
            openPrice: order.openPrice,
            markPrice: order.markPrice,
            markImpliedVolatility: order.markImpliedVolatility,
            underlyingPriceAtOpen: order.underlyingPriceAtOpen,
            delta: order.delta,
            gamma: order.gamma,
            vega: order.vega,
            theta: order.theta,
            comment: order.comment,
            expirationPrice: order.expirationPrice,
            dateForPricing: order.dateForPricing,
            impliedVolatilityForPricing: order.impliedVolatilityForPricing,
            underlyingPriceForPricing: order.underlyingPriceForPricing,
            changeTrackingInitialHashCode: order.changeTrackingInitialHashCode,
            isChanged: order.isChanged,
            ...overrides,
        });
    }

    copyPosition(
        position: PositionModel,
        overrides: Partial<PositionModel> = {},
    ): PositionModel {
        return {
            ...position,
            ...overrides,
        };
    }

    copyPortfolioGroup(
        portfolioGroup: PortfolioGroupModel,
        overrides: Partial<PortfolioGroupModel> = {},
    ): PortfolioGroupModel {
        return {
            id: portfolioGroup.id,
            name: portfolioGroup.name,
            underlyingAsset: {
                ...portfolioGroup.underlyingAsset,
            },
            portfolioSettings: this.copyPortfolioSettings(portfolioGroup.portfolioSettings),
            ...overrides,
        };
    }

    copyPortfolioSettings(settings: PortfolioSettingsModel | null): PortfolioSettingsModel | null {
        return settings !== null
            ? portfolioMappers.mapSettingsModelToModel(settings)
            : null;
    }

    private copyBalanceHistoryEntry(
        entry: PortfolioBalanceHistoryEntryModel,
    ): PortfolioBalanceHistoryEntryModel {
        return {
            ...entry,
        };
    }
}

export const portfolioModelService = new PortfolioModelService();
